import { braceVarToken } from "@swivl-mono/helpers";
import { $applyNodeReplacement, DOMExportOutput, DecoratorNode, EditorConfig, LexicalNode, NodeKey, SerializedLexicalNode, Spread } from "lexical";
import type { JSX } from "react";

import { VariablePill } from "./VariablePill";

/**
 * A `{{Type:Key}}` variable inside the VariableTypeahead editor (ENG-3109), optionally with
 * an inline fallback — `{{Type:Key|"literal"}}` (ENG-3216): the words used when the variable
 * has no value.
 *
 * Rendered as a pill showing the human display name so nobody has to decipher raw
 * tokens; serialized (getTextContent / copy / stored value) as the plain token so what
 * leaves the editor is exactly what the BraceVars parser reads.
 */

export type SerializedVariableNode = Spread<{ id: string; display: string; fallback?: string | null }, SerializedLexicalNode>;

export class VariableNode extends DecoratorNode<JSX.Element> {
  __id: string;
  __display: string;
  __fallback: string | null;

  static getType(): string {
    return "variable";
  }

  static clone(node: VariableNode): VariableNode {
    return new VariableNode(node.__id, node.__display, node.__fallback, node.__key);
  }

  static importJSON(serialized: SerializedVariableNode): VariableNode {
    return $createVariableNode(serialized.id, serialized.display, serialized.fallback ?? null);
  }

  constructor(id: string, display: string, fallback: string | null = null, key?: NodeKey) {
    super(key);
    this.__id = id;
    this.__display = display;
    this.__fallback = fallback;
  }

  exportJSON(): SerializedVariableNode {
    return { ...super.exportJSON(), type: "variable", version: 1, id: this.__id, display: this.__display, fallback: this.__fallback };
  }

  createDOM(config: EditorConfig): HTMLElement {
    const span = document.createElement("span");
    const themeClass = config.theme["variablePill"];
    if (typeof themeClass === "string" && themeClass) span.className = themeClass;
    return span;
  }

  updateDOM(): boolean {
    return false;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement("span");
    element.setAttribute("data-variable-id", this.__id);
    element.textContent = this.getTextContent();
    return { element };
  }

  /** The stored form — copying a pill or reading the editor's text yields the token, fallback included. */
  getTextContent(): string {
    return braceVarToken(this.__id, this.__fallback ?? undefined);
  }

  getId(): string {
    return this.getLatest().__id;
  }

  getDisplay(): string {
    return this.getLatest().__display;
  }

  getFallback(): string | null {
    return this.getLatest().__fallback;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return true;
  }

  isIsolated(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return <VariablePill nodeKey={this.__key} id={this.__id} display={this.__display} fallback={this.__fallback} />;
  }
}

export function $createVariableNode(id: string, display: string, fallback: string | null = null): VariableNode {
  return $applyNodeReplacement(new VariableNode(id, display, fallback));
}

export function $isVariableNode(node: LexicalNode | null | undefined): node is VariableNode {
  return node instanceof VariableNode;
}
